/**
 * Page publique « Payer l'acompte » (lien du mail / du chat devis CADBOT).
 * Le paiement SumUp est créé (ou récupéré) au clic : le bouton du mail fonctionne même si SumUp
 * était indisponible au moment de l'envoi. En cas d'échec : explication + WhatsApp + règlement en boutique.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CreditCard, Loader2, MessageCircle, RefreshCw, Store, XCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL || '';
const DEFAULT_WA = '0694274051';

export default function AcompteDevisStartPage() {
  const { t, formatCurrency } = useLanguage();
  const { id } = useParams();
  const [state, setState] = useState({ status: 'loading', info: null, error: '' });
  const [whatsapp, setWhatsapp] = useState(DEFAULT_WA);

  useEffect(() => {
    axios.get(`${API}/api/public/site-settings`).then((r) => {
      const w = (r.data?.whatsapp || r.data?.telephone || '').toString();
      if (w) setWhatsapp(w);
    }).catch(() => {});
  }, []);

  const start = async () => {
    setState({ status: 'loading', info: null, error: '' });
    try {
      const r = await axios.post(`${API}/api/payments/sumup/chat-devis/start/${encodeURIComponent(id)}`);
      const data = r.data || {};
      if (data.already_paid) { setState({ status: 'paid', info: data, error: '' }); return; }
      if (data.url) {
        setState({ status: 'redirect', info: data, error: '' });
        window.location.assign(data.url);
        return;
      }
      setState({ status: 'error', info: data, error: data.detail || t('acompteStart.errLinkUnavailable') });
    } catch (e) {
      const d = e?.response?.data || {};
      const msg = typeof d.detail === 'string' ? d.detail : (d.detail?.message || d.message || t('acompteStart.errOnlineUnavailable'));
      setState({ status: 'error', info: d, error: msg });
    }
  };

  useEffect(() => { start(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const waDigits = whatsapp.replace(/\D/g, '').replace(/^(?:00)?594/, '').replace(/^0/, '');
  const waLink = `https://wa.me/594${waDigits}?text=${encodeURIComponent(t('acompteStart.waMessage', { id }))}`;
  const amount = state.info?.amount;

  return (
    <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center px-4 py-10" data-testid="acompte-devis-start">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/[.03] p-6 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-red-600/20 flex items-center justify-center"><CreditCard className="w-5 h-5 text-red-400" /></div>
          <div>
            <h1 className="text-lg font-bold">{t('acompteStart.title')}</h1>
            <p className="text-xs text-gray-400">{t('acompteStart.subtitle')}</p>
          </div>
        </div>

        {state.status === 'loading' && (
          <div className="flex items-center gap-2 text-sm text-gray-300"><Loader2 className="w-4 h-4 animate-spin" /> {t('acompteStart.loading')}</div>
        )}
        {state.status === 'redirect' && (
          <div className="text-sm text-gray-300">{t('acompteStart.redirecting')}{amount ? ` (${formatCurrency(amount)})` : ''}… <a className="underline" href={state.info.url}>{t('acompteStart.clickHere')}</a></div>
        )}
        {state.status === 'paid' && (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-200">
            {t('acompteStart.paid')}
            {state.info?.ref_commande && <div className="mt-1 text-xs text-emerald-300/80">{t('acompteStart.orderRef', { ref: state.info.ref_commande })}</div>}
          </div>
        )}
        {state.status === 'error' && (
          <div className="space-y-4">
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100 flex gap-2">
              <XCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-300" />
              <div>
                <div className="font-semibold">{t('acompteStart.errTitle')}</div>
                <div className="text-xs text-amber-200/80 mt-1">{state.error}</div>
              </div>
            </div>
            <div className="grid gap-2">
              <button type="button" onClick={start} className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 hover:bg-red-500 px-4 py-3 text-sm font-semibold"><RefreshCw className="w-4 h-4" /> {t('acompteStart.retry')}</button>
              <a href={waLink} target="_blank" rel="noreferrer" className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 px-4 py-3 text-sm font-semibold"><MessageCircle className="w-4 h-4 text-green-400" /> {t('acompteStart.whatsappPay')}</a>
              <div className="flex items-center gap-2 text-xs text-gray-400 justify-center"><Store className="w-3.5 h-3.5" /> {t('acompteStart.inStore')}</div>
            </div>
          </div>
        )}

        <div className="text-[11px] text-gray-500 text-center">{t('acompteStart.footer')} <Link className="underline" to="/">{t('acompteStart.backToSite')}</Link></div>
      </div>
    </div>
  );
}
